import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { searchClient, ALGOLIA_INDICES, DEFAULT_SEARCH_INDICES, type SearchResult } from '@/config/algolia';
import { useCompanyStore } from '@/stores/company';
import type { SearchResponse } from '@algolia/client-search';

export const useSearchStore = defineStore('search', () => {
  const companyStore = useCompanyStore();
  const isSearchModalOpen = ref(false);
  const searchQuery = ref('');
  const searchResults = ref<SearchResult[]>([]);
  const isLoading = ref(false);
  const isSearching = ref(false);
  const selectedIndices = useLocalStorage<string[]>('searchSelectedIndices', [...DEFAULT_SEARCH_INDICES]);
  const resultsLimit = ref<number>(10);
  const totalResults = ref<number>(0);
  const totalResultsByType = ref<Record<string, number>>({});

  const hasResults = computed(() => searchResults.value.length > 0);

  async function performSearch(query: string, showAll: boolean = false) {
    if (!query.trim()) {
      searchResults.value = [];
      totalResults.value = 0;
      totalResultsByType.value = {};
      return;
    }

    isLoading.value = true;
    isSearching.value = true;
    try {
      const selectedCompanyId = companyStore.selectedCompanyId;
      // Sin compañía activa no se consulta el índice: con la search key pública, un filtro
      // vacío devolvería resultados de todos los tenants (remediación Fase 0 · C6).
      if (!selectedCompanyId) {
        searchResults.value = [];
        totalResults.value = 0;
        totalResultsByType.value = {};
        return;
      }
      const filters = `company:${selectedCompanyId}`;

      const response = await searchClient.search<SearchResult>({
        requests: selectedIndices.value.map(indexName => ({
          indexName,
          query,
          hitsPerPage: showAll ? 100 : resultsLimit.value,
          filters
        }))
      });

      // Crear un mapeo inverso de valores a claves de ALGOLIA_INDICES
      const indexToKey = Object.entries(ALGOLIA_INDICES).reduce((acc, [key, value]) => {
        acc[value] = key as keyof typeof ALGOLIA_INDICES;
        return acc;
      }, {} as Record<string, keyof typeof ALGOLIA_INDICES>);

      const results = (response.results as Array<SearchResponse<SearchResult>>).flatMap((result, index) => {
        const indexName = selectedIndices.value[index];
        const typeKey = indexToKey[indexName];
        
        return result.hits.map(hit => ({
          ...hit,
          type: typeKey
        }));
      });

      // Guardar el número total de resultados
      totalResults.value = (response.results as Array<SearchResponse<SearchResult>>).reduce(
        (sum, result) => sum + (result.nbHits || 0), 
        0
      );

      // Guardar el número total de resultados por tipo
      totalResultsByType.value = (response.results as Array<SearchResponse<SearchResult>>).reduce(
        (acc, result, index) => {
          const indexName = selectedIndices.value[index];
          const typeKey = indexToKey[indexName];
          acc[typeKey] = result.nbHits || 0;
          return acc;
        }, 
        {} as Record<string, number>
      );

      searchResults.value = results;
      
    } catch (error) {
      console.error('Search error:', error);
      searchResults.value = [];
      totalResults.value = 0;
      totalResultsByType.value = {};
    } finally {
      isLoading.value = false;
      isSearching.value = false;
    }
  }

  function setResultsLimit(limit: number) {
    resultsLimit.value = limit;
  }

  function openSearchModal() {
    isSearchModalOpen.value = true;
    isSearching.value = true;
  }

  function closeSearchModal() {
    isSearchModalOpen.value = false;
    isSearching.value = false;
    resetSearch();
  }

  function resetSearch() {
    searchQuery.value = '';
    searchResults.value = [];
    isSearching.value = false;
    totalResults.value = 0;
    totalResultsByType.value = {};
  }

  function toggleIndex(indexValue: string) {
    const idx = selectedIndices.value.indexOf(indexValue);
    if (idx === -1) {
      selectedIndices.value.push(indexValue);
    } else {
      selectedIndices.value.splice(idx, 1);
    }
  }

  function selectAllIndices() {
    selectedIndices.value = Object.values(ALGOLIA_INDICES);
  }

  function deselectAllIndices() {
    selectedIndices.value = [];
  }

  function handleResultSelection() {
    closeSearchModal();
  }

  return {
    isSearchModalOpen,
    searchQuery,
    searchResults,
    isLoading,
    isSearching,
    selectedIndices,
    hasResults,
    resultsLimit,
    totalResults,
    totalResultsByType,
    performSearch,
    setResultsLimit,
    openSearchModal,
    closeSearchModal,
    resetSearch,
    toggleIndex,
    selectAllIndices,
    deselectAllIndices,
    handleResultSelection
  };
});